import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef } from 'react';
import { MarkdownContent } from '@/components/MarkdownContent/MarkdownContent';
import { termsCopy } from '@/Pages/Terms/copy';
import type { ConsentDocumentKind } from '@/types/consent';
import '@/Pages/Terms/TermsPage.sass';

type TermsModalProps = {
  open: boolean;
  document: ConsentDocumentKind | null;
  termsContent: string;
  privacyContent: string;
  onClose: () => void;
  onAccept: () => void;
};

const TITLE_ID = 'terms-modal-title';

function getDocumentTitle(document: ConsentDocumentKind | null): string {
  if (document === 'terms') {
    return termsCopy.checkboxTermsLink;
  }
  if (document === 'privacy') {
    return termsCopy.checkboxPrivacyLink;
  }
  return '';
}

function getDocumentContent(
  document: ConsentDocumentKind | null,
  termsContent: string,
  privacyContent: string,
): string {
  if (document === 'terms') {
    return termsContent;
  }
  if (document === 'privacy') {
    return privacyContent;
  }
  return '';
}

export function TermsModal({
  open,
  document,
  termsContent,
  privacyContent,
  onClose,
  onAccept,
}: TermsModalProps): JSX.Element | null {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const title = getDocumentTitle(document);
  const content = getDocumentContent(document, termsContent, privacyContent);

  useEffect(() => {
    if (open && titleRef.current) {
      titleRef.current.focus();
    }
  }, [open, document]);

  if (!document) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="md"
      aria-labelledby={TITLE_ID}
      className="terms-modal"
      data-testid="terms-modal"
      slotProps={{
        transition: {
          timeout: prefersReducedMotion ? 0 : undefined,
        },
      }}
    >
      <DialogTitle id={TITLE_ID} ref={titleRef} tabIndex={-1}>
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <MarkdownContent content={content} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="terms-modal-close">
          {termsCopy.modalClose}
        </Button>
        <Button
          variant="contained"
          onClick={onAccept}
          data-testid="terms-modal-accept"
        >
          {termsCopy.modalAccept}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
